import { useRef, useEffect, useState } from "react";
import { Input } from "@/shared/components/ui/input";
import { Search, X, Loader2 } from "lucide-react";

export interface ClientOption {
  id: number;
  name: string;
  document: string;
}

interface ClientSearchInputProps {
  value: string;
  /** `null` enquanto nenhum cliente foi confirmado — controla exibição do botão limpar. */
  selectedId: number | null;
  options: ClientOption[];
  loading: boolean;
  onSearch: (term: string) => void;
  onSelect: (client: ClientOption) => void;
  onClear: () => void;
}

/**
 * Campo de busca com autocomplete para seleção de cliente.
 *
 * O estado de busca e a lista de opções são gerenciados externamente — este
 * componente apenas exibe e delega interações via callbacks. O dropdown fecha
 * automaticamente ao clicar fora do componente.
 *
 * @example
 * <ClientSearchInput
 *   value={clientSearchTerm}
 *   selectedId={selectedClientId}
 *   options={clientOptions}
 *   loading={clientSearchLoading}
 *   onSearch={handleClientSearch}
 *   onSelect={handleClientSelect}
 *   onClear={handleClientClear}
 * />
 */
export const ClientSearchInput = ({
  value,
  selectedId,
  options,
  loading,
  onSearch,
  onSelect,
  onClear,
}: ClientSearchInputProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showDropdown =
    open && (loading || options.length > 0 || value.trim().length > 0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(e.target.value);
    setOpen(true);
  };

  const handleSelect = (client: ClientOption) => {
    onSelect(client);
    setOpen(false);
  };

  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Buscar cliente por nome..."
          value={value}
          onChange={handleChange}
          onFocus={() => {
            if (options.length > 0 || loading) setOpen(true);
          }}
          className={`pl-9 ${selectedId ? "pr-9" : ""}`}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
        {selectedId && !loading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Limpar seleção"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {selectedId && (
        <p className="mt-1 text-xs text-muted-foreground">
          Cliente selecionado:{" "}
          <span className="font-medium text-foreground">{value}</span>
        </p>
      )}

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-md">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Buscando clientes...
            </div>
          ) : options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              Nenhum cliente encontrado
            </p>
          ) : (
            <ul className="max-h-48 overflow-y-auto py-1">
              {options.map((client) => (
                <li key={client.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(client)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <span className="font-medium">{client.name}</span>
                    <span className="ml-2 text-muted-foreground text-xs">
                      {client.document}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
